import React from 'react';
import { Check } from 'lucide-react';

/**
 * InstructionsDisplay - A beautiful, organized way to display recipe instructions
 * 
 * Parses various instruction formats:
 * - "1. Do this 2. Do that" 
 * - "1) Do this 2) Do that"
 * - Newline separated steps
 * - Plain text (displayed as single block)
 */
export default function InstructionsDisplay({ instructions, className = '' }) {
  if (!instructions) return null;

  // Parse instructions into steps
  const parseInstructions = (input) => {
    // Handle array format (from swap endpoint and new recipes)
    if (Array.isArray(input)) {
      return input.map((step, index) => ({
        number: index + 1,
        text: step.trim()
      }));
    }
    
    // Handle string format (legacy)
    if (!input || typeof input !== 'string') return [];
    
    // Clean up the text
    let cleanText = input.trim();
    
    // Try to split by numbered patterns: "1. ", "2. ", "3. " etc.
    // This handles instructions like "1. Do this 2. Do that 3. Do more"
    const parts = cleanText.split(/\s+(?=\d+\.\s+)/).filter(p => p.trim());
    
    if (parts.length > 1) {
      return parts.map((part, index) => {
        // Remove the leading number and period
        const text = part.replace(/^\d+\.\s*/, '').trim();
        return {
          number: index + 1,
          text: text
        };
      });
    }
    
    // Try splitting by newlines if no numbered pattern found
    const lines = cleanText.split('\n').map(l => l.trim()).filter(l => l);
    
    if (lines.length > 1) {
      return lines.map((line, index) => ({
        number: index + 1,
        text: line.replace(/^[-•*\d+.)\s]+/, '').trim() // Remove bullets or numbers
      }));
    }
    
    // Return as single step if nothing else works
    return [{
      number: 1,
      text: cleanText
    }];
  };

  const steps = parseInstructions(instructions);

  // If only one step and it's short, show compact view
  if (steps.length === 1 && steps[0].text.length < 100) {
    return (
      <div className={`bg-slate-50 rounded-xl p-4 ${className}`}>
        <p className="text-gray-700 text-sm leading-relaxed">{steps[0].text}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {steps.map((step, index) => (
        <div 
          key={index}
          className="flex gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-blue-200 hover:bg-blue-50/50 transition-all group"
        >
          {/* Step number circle */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white border-2 border-slate-300 flex items-center justify-center text-sm font-bold text-slate-700 group-hover:border-blue-400 group-hover:text-blue-600 transition-colors shadow-sm">
            {step.number}
          </div>
          
          {/* Step content */}
          <div className="flex-1 pt-1">
            <p className="text-sm text-gray-800 leading-relaxed">
              {step.text}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Compact version for recipe cards
 */
export function InstructionsPreview({ instructions, maxSteps = 3 }) {
  if (!instructions) return null;

  const parseInstructions = (input) => {
    // Handle array format (from swap endpoint and new recipes)
    if (Array.isArray(input)) {
      return input.map((step, index) => ({
        number: index + 1,
        text: step.trim()
      }));
    }
    
    // Handle string format (legacy)
    if (!input || typeof input !== 'string') return [];
    let cleanText = input.trim();
    
    // Split by numbered patterns like "1. ", "2. ", "3. "
    const parts = cleanText.split(/\s+(?=\d+\.\s+)/).filter(p => p.trim());
    
    if (parts.length > 1) {
      return parts.map((part, index) => {
        const text = part.replace(/^\d+\.\s*/, '').trim();
        return {
          number: index + 1,
          text: text
        };
      });
    }
    
    const lines = cleanText.split('\n').map(l => l.trim()).filter(l => l);
    if (lines.length > 1) {
      return lines.map((line, index) => ({
        number: index + 1,
        text: line.replace(/^[-•*\d+.)\s]+/, '').trim()
      }));
    }
    
    return [{ number: 1, text: cleanText }];
  };

  const steps = parseInstructions(instructions);
  const displaySteps = steps.slice(0, maxSteps);
  const remaining = steps.length - maxSteps;

  return (
    <div className="space-y-1.5">
      {displaySteps.map((step, index) => (
        <div key={index} className="flex gap-2 text-xs text-gray-600">
          <span className="font-medium text-gray-400 w-4">{step.number}.</span>
          <span className="line-clamp-1">{step.text}</span>
        </div>
      ))}
      {remaining > 0 && (
        <p className="text-xs text-gray-400 pl-6">+{remaining} more steps</p>
      )}
    </div>
  );
}
